import { Spinner, useToast } from '@chakra-ui/react'
import type React from 'react'
import { useState } from 'react'
import Loading03Icon from '../../../../../../assets/loading-03-stroke-rounded'
import PersonalDetailItem from '../../../../../../components/PersonalDetailItem'
import {
  useBackupCaseResolveMutation,
  useBackupCustomerExitMutation,
  useBackupPepConfirmMutation,
  useBackupRestrictionDecisionMutation,
  useBackupReviewRequiredMutation,
  useBackupSofRequestCloseMutation,
  useBackupStrFileMutation,
  useBackupTierChangeMutation,
  useGetComplianceInvestorQuery,
  useRescoreComplianceInvestorMutation,
  useResetComplianceInvestorMutation,
  useResyncComplianceInvestorMutation,
  useUpdateComplianceInvestorAnswersMutation,
} from '../../../../../../redux/services/coreApi'
import type {
  BackupCaseDecision,
  BackupRestrictionKindWire,
  BackupSofOutcome,
  BackupTierWire,
} from '../../../../../../types/complianceWire'
import type {
  ComplianceCaseAdminDto,
  ComplianceInvestorDto,
  ComplianceOnboardingAnswersDto,
  ComplianceRestrictionAdminDto,
  ProductSelection,
  Q4BBrokerUse,
  Q4IInvestUse,
  Q5Frequency,
  Q6AnnualVolume,
  Q7SourceOfFunds,
  Q8FundsOwnership,
  Q9Occupation,
  Q10PepRca,
  RestrictionKind,
  RiskAssessmentSummaryDto,
} from '../../../../../../types/coreApi-types'
import {
  NATIONALITY_OPTIONS,
  Q0_OPTIONS,
  Q4B_OPTIONS,
  Q4I_OPTIONS,
  Q5_OPTIONS,
  Q6_OPTIONS,
  Q7_OPTIONS,
  Q8_OPTIONS,
  Q9_OPTIONS,
  Q10_OPTIONS,
  type QuestionnaireOption,
} from '../../../../../../utils/complianceQuestionnaireOptions'
import {
  DateFormatOption,
  formatUtcDate,
} from '../../../../../../utils/formatters'

interface ComplianceProps {
  userId: string
}

const Compliance: React.FC<ComplianceProps> = ({ userId }) => {
  const { data, isLoading, isError } = useGetComplianceInvestorQuery(userId)
  const [rescoreComplianceInvestor, { isLoading: isRescoring }] =
    useRescoreComplianceInvestorMutation()
  const [resyncComplianceInvestor, { isLoading: isResyncing }] =
    useResyncComplianceInvestorMutation()
  const toast = useToast()

  const handleRescore = async () => {
    try {
      const result = await rescoreComplianceInvestor(userId).unwrap()
      toast({
        title: 'Compliance re-scored',
        description: `New state: ${result.newState}${
          result.newTier ? ` (${result.newTier})` : ''
        }`,
        status: 'success',
        position: 'top-right',
      })
    } catch {
      toast({
        title: 'Could not re-score compliance',
        description:
          'Check that the customer has submitted the questionnaire and has completed KYC.',
        status: 'error',
        position: 'top-right',
      })
    }
  }

  const handleResync = async () => {
    try {
      const result = await resyncComplianceInvestor(userId).unwrap()
      toast({
        title: result.crmAccepted
          ? 'Resynced to CRM'
          : 'Resync attempted, CRM did not accept',
        description: result.crmAccepted
          ? `Re-emitted ${result.eventsAccepted}/${result.eventsAttempted} CRM events for assessment ${result.resyncedAssessmentId.slice(0, 8)}…`
          : `CRM accepted ${result.eventsAccepted}/${result.eventsAttempted} events. Check the API logs for the rejection reason.`,
        status: result.crmAccepted ? 'success' : 'warning',
        position: 'top-right',
      })
    } catch {
      toast({
        title: 'Could not resync to CRM',
        description:
          'No assessment exists for this investor yet, or the request failed.',
        status: 'error',
        position: 'top-right',
      })
    }
  }

  if (isLoading) {
    return (
      <div className="mt-10 flex justify-center flex-col items-center">
        <Spinner size="lg" />
        <p className="text-description mt-4">Loading compliance data...</p>
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div className="space-y-3">
        <h1 className="h1">Compliance</h1>
        <div className="card">
          <p className="text-description">
            Could not load compliance data for this client.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4 pb-10 w-full max-w-7xl mx-auto">
      <ComplianceHero
        data={data}
        isResyncing={isResyncing}
        isRescoring={isRescoring}
        onResync={handleResync}
        onRescore={handleRescore}
      />
      <OverviewGrid data={data} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <AnswersCard
          userId={userId}
          answers={data.currentAnswers}
          nationalityMismatch={data.nationalityMismatch}
          pepDeclarationMismatch={data.pepDeclarationMismatch}
        />
        <CurrentAssessmentCard assessment={data.currentAssessment} />
      </div>
      <AssessmentHistoryCard
        assessments={data.assessmentHistory}
        currentId={data.currentAssessmentId ?? null}
      />
      <RestrictionsCard userId={userId} restrictions={data.restrictions} />
      <CasesCard userId={userId} cases={data.cases} />
      <BackupActionsSection userId={userId} data={data} />
      <ResetCard userId={userId} />
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// Shared building blocks
// ──────────────────────────────────────────────────────────────────────────

const SectionHeader: React.FC<{
  title: string
  description?: string
  right?: React.ReactNode
}> = ({ title, description, right }) => (
  <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
    <div>
      <h2 className="text-default-base font-semi">{title}</h2>
      {description && (
        <p className="text-description mt-1 max-w-2xl">{description}</p>
      )}
    </div>
    {right}
  </div>
)

const StatePill: React.FC<{ state: ComplianceInvestorDto['state'] }> = ({
  state,
}) => {
  const cls =
    state === 'Active'
      ? 'status-badge-success'
      : state === 'Exited' || state === 'Rejected'
        ? 'status-badge-cancelled'
        : state === 'ActivePendingMlro' || state === 'Edd'
          ? 'status-badge-processing'
          : state === 'Unscored'
            ? 'status-badge-pending'
            : 'status-badge-neutral'
  return <span className={cls}>{state}</span>
}

const TierPill: React.FC<{ tier: string | null | undefined }> = ({ tier }) => {
  if (!tier) return <span className="status-badge-neutral">No tier</span>
  const lower = tier.toLowerCase()
  const cls =
    lower === 'high'
      ? 'status-badge-cancelled'
      : lower === 'medium'
        ? 'status-badge-processing'
        : lower === 'low'
          ? 'status-badge-success'
          : 'status-badge-neutral'
  return <span className={cls}>{tier}</span>
}

const CaseStatusPill: React.FC<{ status: string }> = ({ status }) => {
  const cls =
    status === 'Open'
      ? 'status-badge-processing'
      : status === 'ResolvedApproved'
        ? 'status-badge-success'
        : status === 'ResolvedRejected'
          ? 'status-badge-cancelled'
          : 'status-badge-neutral'
  return <span className={cls}>{status}</span>
}

interface LoadingButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  variant?: 'primary' | 'secondary' | 'danger' | 'warning' | 'ghost'
  isLoading?: boolean
  size?: 'sm' | 'md'
  className?: string
}

const LoadingButton: React.FC<LoadingButtonProps> = ({
  variant = 'primary',
  isLoading,
  size = 'md',
  className,
  children,
  disabled,
  ...rest
}) => {
  const base =
    'inline-flex items-center justify-center gap-2 rounded-md font-semi outline-none focus-border transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap'
  const sizing =
    size === 'sm' ? 'h-[34px] px-3 text-xs' : 'h-[40px] px-4 text-xs'
  const variantCls =
    variant === 'primary'
      ? 'bg-bg-accent text-white hover:opacity-90'
      : variant === 'secondary'
        ? 'bg-bg-component text-brand-light-text-default hover:bg-brand-light-card-border'
        : variant === 'danger'
          ? 'bg-brand-light-red text-white hover:opacity-90'
          : variant === 'warning'
            ? 'bg-transparent text-brand-light-red border-solid border-[1px] border-brand-light-red hover:bg-brand-light-red hover:text-white'
            : 'bg-transparent text-brand-light-text-default border-solid border-[1px] border-brand-light-card-border hover:bg-brand-light-background'
  return (
    <button
      {...rest}
      disabled={disabled || isLoading}
      className={`${base} ${sizing} ${variantCls} ${className ?? ''}`}
    >
      {isLoading && <Loading03Icon className="w-4 h-4 animate-spin" />}
      {children}
    </button>
  )
}

const StyledSelect: React.FC<
  React.SelectHTMLAttributes<HTMLSelectElement> & { fullWidth?: boolean }
> = ({ className, fullWidth, ...rest }) => (
  <select
    {...rest}
    className={`input bg-brand-light-background hover:bg-brand-light-card-border text-xs pr-3 cursor-pointer ${
      fullWidth ? 'w-full' : ''
    } ${className ?? ''}`}
  />
)

const StyledInput: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = ({
  className,
  ...rest
}) => <input {...rest} className={`input text-xs ${className ?? ''}`} />

const StyledTextarea: React.FC<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>
> = ({ className, ...rest }) => (
  <textarea
    {...rest}
    className={`input pt-2.5 min-h-[60px] text-xs transition-none ${className ?? ''}`}
  />
)

// ──────────────────────────────────────────────────────────────────────────
// Hero header
// ──────────────────────────────────────────────────────────────────────────

const ComplianceHero: React.FC<{
  data: ComplianceInvestorDto
  isResyncing: boolean
  isRescoring: boolean
  onResync: () => void
  onRescore: () => void
}> = ({ data, isResyncing, isRescoring, onResync, onRescore }) => (
  <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
    <div className="flex flex-col gap-2">
      <h1 className="color-text-default font-semi text-xl">Compliance</h1>
      <div className="flex flex-wrap items-center gap-2">
        <StatePill state={data.state} />
        <TierPill tier={data.tier} />
        {data.vulnerabilityTag && (
          <span className="status-badge-warning">Vulnerability tag</span>
        )}
        {data.pepConfirmed && (
          <span
            className="status-badge-cancelled"
            title="Compliance-confirmed PEP. Onboarding forces EDD regardless of the customer's Q10 answer."
          >
            PEP confirmed
          </span>
        )}
        {data.pepDeclarationMismatch && (
          <span
            className="status-badge-warning"
            title="Screening/confirmation says PEP, but the customer declared 'No' to Q10 — a recorded non-disclosure."
          >
            PEP declaration mismatch
          </span>
        )}
        {data.strFiledAt && (
          <span className="status-badge-cancelled">STR filed</span>
        )}
      </div>
    </div>
    <div className="flex flex-wrap items-center gap-2">
      <LoadingButton
        variant="secondary"
        size="sm"
        isLoading={isResyncing}
        disabled={!data.currentAssessmentId}
        onClick={onResync}
        title="Re-emit the latest assessment.created event to CRM. Use after a transient CRM outage."
      >
        Resync to CRM
      </LoadingButton>
      <LoadingButton
        variant="primary"
        size="sm"
        isLoading={isRescoring}
        disabled={!data.currentAnswers}
        onClick={onRescore}
      >
        Re-score customer
      </LoadingButton>
    </div>
  </div>
)

// ──────────────────────────────────────────────────────────────────────────
// Overview KPI grid
// ──────────────────────────────────────────────────────────────────────────

const OverviewGrid: React.FC<{ data: ComplianceInvestorDto }> = ({ data }) => {
  const tiles: Array<{ label: string; value: string }> = [
    { label: 'State', value: data.state },
    { label: 'Risk tier', value: data.tier ?? '—' },
    {
      label: 'Vulnerability tag',
      value: data.vulnerabilityTag ? 'Yes' : 'No',
    },
    {
      label: 'Rule set',
      value:
        data.answersRuleSetName && data.answersRuleSetVersion
          ? `${data.answersRuleSetName} ${data.answersRuleSetVersion}`
          : '—',
    },
    {
      // The second staleness axis (ADR-0014): a customer can be on the active rule set
      // and still be scored against a superseded country classification.
      label: 'Country risk register',
      value:
        data.answersCountryRiskRegisterName && data.answersCountryRiskRegisterVersion
          ? `${data.answersCountryRiskRegisterName} ${data.answersCountryRiskRegisterVersion}`
          : '—',
    },
    {
      label: 'Next review due',
      value: data.nextReviewDueAt
        ? formatUtcDate(data.nextReviewDueAt, DateFormatOption.DATE)
        : '—',
    },
    {
      label: 'STR filed',
      value: data.strFiledAt
        ? formatUtcDate(data.strFiledAt, DateFormatOption.PARTIAL)
        : '—',
    },
  ]
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
      {tiles.map(t => (
        <div
          key={t.label}
          className="rounded-lg border border-solid border-brand-light-card-border bg-brand-light-card dark:bg-brand-dark-card dark:border-brand-dark-card-border p-3 flex flex-col justify-between min-h-[78px]"
        >
          <p className="text-description text-[11px] uppercase tracking-wide font-medium">
            {t.label}
          </p>
          <p className="text-default-small mt-1 truncate">{t.value}</p>
        </div>
      ))}
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// Read-only data cards
// ──────────────────────────────────────────────────────────────────────────

const AnswersCard: React.FC<{
  userId: string
  answers: ComplianceOnboardingAnswersDto | null | undefined
  nationalityMismatch?: boolean
  pepDeclarationMismatch?: boolean
}> = ({ userId, answers, nationalityMismatch, pepDeclarationMismatch }) => {
  const [isEditing, setIsEditing] = useState(false)

  return (
    <div className="card gap-3">
      <SectionHeader
        title="Questionnaire answers"
        right={
          answers && !isEditing ? (
            <LoadingButton
              variant="secondary"
              size="sm"
              onClick={() => setIsEditing(true)}
            >
              Edit answers
            </LoadingButton>
          ) : undefined
        }
      />
      {!answers ? (
        <p className="text-description">
          Customer has not submitted the risk-assessment questionnaire yet.
        </p>
      ) : isEditing ? (
        <AnswersEditor
          userId={userId}
          answers={answers}
          onClose={() => setIsEditing(false)}
        />
      ) : (
        <div className="flex flex-col gap-2.5">
          <PersonalDetailItem
            title="Q0 — Products"
            value={answers.q0Products.join(', ') || '—'}
          />
          <PersonalDetailItem
            title="Q4 Broker — Use"
            value={answers.q4Broker.join(', ') || '—'}
          />
          <PersonalDetailItem
            title="Q4 Invest — Use"
            value={answers.q4Invest.join(', ') || '—'}
          />
          <PersonalDetailItem
            title="Q4 Wealth — Strategy (legacy, no longer scored)"
            value={answers.q4Wealth.join(', ') || '—'}
          />
          <PersonalDetailItem title="Q5 — Frequency" value={answers.q5} />
          <PersonalDetailItem title="Q6 — Annual volume" value={answers.q6} />
          <PersonalDetailItem
            title="Q7 — Source of funds"
            value={answers.q7.join(', ') || '—'}
          />
          <PersonalDetailItem title="Q8 — Funds ownership" value={answers.q8} />
          <PersonalDetailItem title="Q9 — Occupation" value={answers.q9} />
          <PersonalDetailItem
            title="Q10 — PEP / RCA"
            value={
              answers.q10 +
              (pepDeclarationMismatch
                ? ' ⚠️ contradicts screened/confirmed PEP'
                : '')
            }
          />
          <PersonalDetailItem
            title="Nationality (declared)"
            value={
              answers.declaredNationalityIso
                ? answers.declaredNationalityIso +
                  (nationalityMismatch ? ' ⚠️ mismatch vs ID document' : '')
                : '—'
            }
          />
        </div>
      )}
    </div>
  )
}

// MLRO inline editor: pre-filled from the stored answers, edit one or more, save.
// Saving overwrites the stored answers and always re-scores (the API does this in
// one call), so the customer keeps their onboarding — no full reset required.
const AnswersEditor: React.FC<{
  userId: string
  answers: ComplianceOnboardingAnswersDto
  onClose: () => void
}> = ({ userId, answers, onClose }) => {
  const toast = useToast()
  const [updateAnswers, { isLoading }] =
    useUpdateComplianceInvestorAnswersMutation()

  const [q0Products, setQ0Products] = useState<ProductSelection[]>(
    answers.q0Products
  )
  const [q4Broker, setQ4Broker] = useState<Q4BBrokerUse[]>(answers.q4Broker)
  const [q4Invest, setQ4Invest] = useState<Q4IInvestUse[]>(answers.q4Invest)
  const [q5, setQ5] = useState<Q5Frequency>(answers.q5)
  const [q6, setQ6] = useState<Q6AnnualVolume>(answers.q6)
  const [q7, setQ7] = useState<Q7SourceOfFunds[]>(answers.q7)
  const [q8, setQ8] = useState<Q8FundsOwnership>(answers.q8)
  const [q9, setQ9] = useState<Q9Occupation>(answers.q9)
  const [q10, setQ10] = useState<Q10PepRca>(answers.q10)
  const [nationality, setNationality] = useState<string>(
    answers.declaredNationalityIso ?? ''
  )

  const showQ4B = q0Products.includes('Broker')
  const showQ4I = q0Products.includes('Invest')

  const validate = (): string | null => {
    if (q0Products.length === 0)
      return 'Pick at least one product (Broker or Invest).'
    if (showQ4B && q4Broker.length === 0)
      return 'Pick at least one Broker use case.'
    if (showQ4I && q4Invest.length === 0)
      return 'Pick at least one Invest funding/exit method.'
    if (q7.length === 0) return 'Pick at least one source of funds.'
    if (!nationality) return 'Select a nationality.'
    return null
  }

  const handleSave = async () => {
    const error = validate()
    if (error) {
      toast({
        title: 'Cannot save answers',
        description: error,
        status: 'error',
        position: 'top-right',
      })
      return
    }

    try {
      const result = await updateAnswers({
        investorId: userId,
        body: {
          q0Products,
          // Drop Q4 selections for products no longer chosen so stored answers
          // stay consistent with Q0 (mirrors the customer questionnaire).
          q4Broker: showQ4B ? q4Broker : [],
          q4Invest: showQ4I ? q4Invest : [],
          // v7.9: the Wealth product / Q4-X strategy is gone (the Veli-migration
          // risk is derived server-side). Field retained for v7.8 replay; empty.
          q4Wealth: [],
          q5,
          q6,
          q7,
          q8,
          q9,
          q10,
          declaredNationalityIso: nationality,
        },
      }).unwrap()
      toast({
        title: 'Answers updated and re-scored',
        description: `New state: ${result.newState}${
          result.newTier ? ` (${result.newTier})` : ''
        }`,
        status: 'success',
        position: 'top-right',
      })
      onClose()
    } catch {
      toast({
        title: 'Could not update answers',
        description:
          'The edit was rejected. Check that the customer has submitted the questionnaire and completed KYC.',
        status: 'error',
        position: 'top-right',
      })
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-description text-xs">
        Editing the answers will immediately re-score the customer against the
        active rule set. Restrictions, cases, and onboarding completion are
        kept.
      </p>

      <EditCheckboxGroup
        label="Q0 — Products"
        options={Q0_OPTIONS}
        selected={q0Products}
        onChange={setQ0Products}
      />
      {showQ4B && (
        <EditCheckboxGroup
          label="Q4 Broker — Use"
          options={Q4B_OPTIONS}
          selected={q4Broker}
          onChange={setQ4Broker}
        />
      )}
      {showQ4I && (
        <EditCheckboxGroup
          label="Q4 Invest — Use"
          options={Q4I_OPTIONS}
          selected={q4Invest}
          onChange={setQ4Invest}
        />
      )}
      <EditSelect
        label="Q5 — Frequency"
        options={Q5_OPTIONS}
        value={q5}
        onChange={setQ5}
      />
      <EditSelect
        label="Q6 — Annual volume"
        options={Q6_OPTIONS}
        value={q6}
        onChange={setQ6}
      />
      <EditCheckboxGroup
        label="Q7 — Source of funds"
        options={Q7_OPTIONS}
        selected={q7}
        onChange={setQ7}
      />
      <EditSelect
        label="Q8 — Funds ownership"
        options={Q8_OPTIONS}
        value={q8}
        onChange={setQ8}
      />
      <EditSelect
        label="Q9 — Occupation"
        options={Q9_OPTIONS}
        value={q9}
        onChange={setQ9}
      />
      <EditSelect
        label="Q10 — PEP / RCA"
        options={Q10_OPTIONS}
        value={q10}
        onChange={setQ10}
      />
      <EditSelect
        label="Nationality (declared)"
        options={NATIONALITY_OPTIONS}
        value={nationality}
        onChange={setNationality}
        placeholder="Select a nationality"
      />

      <div className="flex items-center gap-2">
        <LoadingButton
          variant="primary"
          size="sm"
          isLoading={isLoading}
          onClick={handleSave}
        >
          Save & re-score
        </LoadingButton>
        <LoadingButton
          variant="ghost"
          size="sm"
          disabled={isLoading}
          onClick={onClose}
        >
          Cancel
        </LoadingButton>
      </div>
    </div>
  )
}

const EditCheckboxGroup = <T extends string>({
  label,
  options,
  selected,
  onChange,
}: {
  label: string
  options: QuestionnaireOption<T>[]
  selected: T[]
  onChange: (next: T[]) => void
}) => {
  const toggle = (value: T) =>
    onChange(
      selected.includes(value)
        ? selected.filter(v => v !== value)
        : [...selected, value]
    )
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-default-small font-semi">{label}</p>
      <div className="flex flex-col gap-1.5">
        {options.map(option => (
          <label
            key={option.value}
            className="flex items-start gap-2.5 cursor-pointer text-xs"
          >
            <input
              type="checkbox"
              className="mt-0.5"
              checked={selected.includes(option.value)}
              onChange={() => toggle(option.value)}
            />
            <span>{option.label}</span>
          </label>
        ))}
      </div>
    </div>
  )
}

const EditSelect = <T extends string>({
  label,
  options,
  value,
  onChange,
  placeholder,
}: {
  label: string
  options: QuestionnaireOption<T>[]
  value: T | ''
  onChange: (next: T) => void
  // When set, renders a leading empty option so an unset value shows the
  // placeholder instead of the browser silently falling back to the first
  // option (which made an undeclared nationality look like a real choice).
  placeholder?: string
}) => (
  <div className="flex flex-col gap-1.5">
    <p className="text-default-small font-semi">{label}</p>
    <StyledSelect
      fullWidth
      value={value}
      onChange={e => onChange(e.target.value as T)}
    >
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map(o => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </StyledSelect>
  </div>
)

const CurrentAssessmentCard: React.FC<{
  assessment: RiskAssessmentSummaryDto | null | undefined
}> = ({ assessment }) => (
  <div className="card gap-3">
    <SectionHeader title="Current assessment" />
    {!assessment ? (
      <p className="text-description">No scored assessment yet.</p>
    ) : (
      <div className="flex flex-col gap-2.5">
        <PersonalDetailItem
          title="Scored at"
          value={formatUtcDate(assessment.createdAt, DateFormatOption.PARTIAL)}
        />
        <PersonalDetailItem title="Kind" value={assessment.kind} />
        <PersonalDetailItem title="Type" value={assessment.assessmentType} />
        <PersonalDetailItem title="Tier" value={assessment.tier ?? '—'} />
        <PersonalDetailItem
          title="Total points"
          value={
            assessment.totalPoints != null
              ? String(assessment.totalPoints)
              : '—'
          }
        />
        <PersonalDetailItem
          title="Points (D1/D2/D3/D4)"
          value={[
            assessment.d1Points,
            assessment.d2Points,
            assessment.d3Points,
            assessment.d4Points,
          ]
            .map(p => (p != null ? String(p) : '—'))
            .join(' / ')}
        />
        <PersonalDetailItem
          title="Vulnerability tag"
          value={
            assessment.vulnerabilityTag
              ? `Yes (${assessment.vulnerabilityTagPoints ?? 0} pts)`
              : 'No'
          }
        />
        <PersonalDetailItem
          title="EDD triggered"
          value={assessment.eddTriggered ? 'Yes' : 'No'}
        />
        <PersonalDetailItem
          title="EDD reasons"
          value={assessment.eddReasons.join(', ') || '—'}
        />
        <PersonalDetailItem
          title="Reject reasons"
          value={assessment.rejectReasons.join(', ') || '—'}
        />
        <PersonalDetailItem
          title="Counterparty risk"
          value={assessment.counterpartyRiskFlag ?? '—'}
        />
        <PersonalDetailItem
          title="Cat A / B / C points"
          value={[
            assessment.catAPoints,
            assessment.catBPoints,
            assessment.catCPoints,
          ]
            .map(p => (p != null ? String(p) : '—'))
            .join(' / ')}
        />
        <PersonalDetailItem
          title="STR bypass"
          value={assessment.strBypass ? 'Yes' : 'No'}
        />
        <PersonalDetailItem
          title="Rule set"
          value={`${assessment.ruleSetName} ${assessment.ruleSetVersion}`}
        />
        {/* The country risk register the geographic legs were scored against. Its own
            version axis (ADR-0014), so the rule set alone doesn't say which country
            classification produced this D4. Absent on assessments scored before v7.11. */}
        <PersonalDetailItem
          title="Country risk register"
          value={
            assessment.countryRiskRegisterName && assessment.countryRiskRegisterVersion
              ? `${assessment.countryRiskRegisterName} ${assessment.countryRiskRegisterVersion}`
              : '—'
          }
        />
      </div>
    )}
  </div>
)

const AssessmentHistoryCard: React.FC<{
  assessments: RiskAssessmentSummaryDto[]
  currentId: string | null
}> = ({ assessments, currentId }) => (
  <div className="card">
    <SectionHeader
      title="Assessment history"
      description="Every prior scoring run is retained for audit. The current assessment drives live state."
    />
    {assessments.length === 0 ? (
      <p className="text-description">No assessments recorded.</p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-default-small">
          <thead>
            <tr className="text-left text-description border-b border-solid border-brand-light-card-border">
              <th className="py-2.5 pr-4 font-semi">Scored at</th>
              <th className="py-2.5 pr-4 font-semi">Type</th>
              <th className="py-2.5 pr-4 font-semi">Kind</th>
              <th className="py-2.5 pr-4 font-semi">Tier</th>
              <th className="py-2.5 pr-4 font-semi">Points</th>
              <th className="py-2.5 pr-4 font-semi">EDD</th>
              <th className="py-2.5 pr-4 font-semi">Rule set</th>
              <th className="py-2.5 font-semi">Current</th>
            </tr>
          </thead>
          <tbody>
            {assessments.map(a => (
              <tr
                key={a.id}
                className="border-b border-solid border-brand-light-card-border last:border-b-0 hover:bg-brand-light-background/60 transition-colors"
              >
                <td className="py-2.5 pr-4">
                  {formatUtcDate(a.createdAt, DateFormatOption.PARTIAL)}
                </td>
                <td className="py-2.5 pr-4">{a.assessmentType}</td>
                <td className="py-2.5 pr-4">{a.kind}</td>
                <td className="py-2.5 pr-4">
                  <TierPill tier={a.tier} />
                </td>
                <td className="py-2.5 pr-4">
                  {a.totalPoints != null ? a.totalPoints : '—'}
                </td>
                <td className="py-2.5 pr-4">
                  {a.eddTriggered ? (
                    <span className="status-badge-warning">Yes</span>
                  ) : (
                    <span className="text-description">No</span>
                  )}
                </td>
                <td className="py-2.5 pr-4 text-description">
                  {a.ruleSetName} {a.ruleSetVersion}
                </td>
                <td className="py-2.5">
                  {a.id === currentId ? (
                    <span className="status-badge-success">Current</span>
                  ) : (
                    <span className="text-description">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
)

// ──────────────────────────────────────────────────────────────────────────
// Restrictions
// ──────────────────────────────────────────────────────────────────────────

const restrictionKindToWire: Record<
  RestrictionKind,
  BackupRestrictionKindWire
> = {
  DepositBlock: 'deposit_block',
  WithdrawalBlock: 'withdrawal_block',
  TradeBlock: 'trade_block',
  AccountFreeze: 'account_freeze',
  TxOver5kBlock: 'tx_over_5k_block',
  BuyBlock: 'buy_block',
}

const RestrictionsCard: React.FC<{
  userId: string
  restrictions: ComplianceRestrictionAdminDto[]
}> = ({ userId, restrictions }) => {
  const toast = useToast()
  const [decision, { isLoading }] = useBackupRestrictionDecisionMutation()
  const [pendingId, setPendingId] = useState<string | null>(null)

  const activeCount = restrictions.filter(r => r.isActive).length

  const handleLift = async (r: ComplianceRestrictionAdminDto) => {
    setPendingId(r.id)
    try {
      await decision({
        investorId: userId,
        body: {
          kind: restrictionKindToWire[r.kind],
          action: 'lift',
          reasonCode: 'BACKUP_OPERATOR_LIFT',
        },
      }).unwrap()
      toast({
        title: 'Restriction lifted',
        description: `${r.kind} (${r.source}) lifted via backup action.`,
        status: 'success',
        position: 'top-right',
      })
    } catch {
      toast({
        title: 'Could not lift restriction',
        description:
          'Only restrictions with source ManualMlro can be lifted via this path. Use case-resolve for pending_mlro restrictions.',
        status: 'error',
        position: 'top-right',
      })
    } finally {
      setPendingId(null)
    }
  }

  return (
    <div className="card">
      <SectionHeader
        title="Restrictions"
        right={
          activeCount > 0 ? (
            <span className="status-badge-cancelled">{activeCount} active</span>
          ) : (
            <span className="status-badge-success">None active</span>
          )
        }
      />
      {restrictions.length === 0 ? (
        <p className="text-description">No restrictions on record.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-default-small">
            <thead>
              <tr className="text-left text-description border-b border-solid border-brand-light-card-border">
                <th className="py-2.5 pr-4 font-semi">Kind</th>
                <th className="py-2.5 pr-4 font-semi">Source</th>
                <th className="py-2.5 pr-4 font-semi">Reason code</th>
                <th className="py-2.5 pr-4 font-semi">Applied</th>
                <th className="py-2.5 pr-4 font-semi">Lifted</th>
                <th className="py-2.5 pr-4 font-semi">Status</th>
                <th className="py-2.5 font-semi">Action</th>
              </tr>
            </thead>
            <tbody>
              {restrictions.map(r => (
                <tr
                  key={r.id}
                  className="border-b border-solid border-brand-light-card-border last:border-b-0 hover:bg-brand-light-background/60 transition-colors"
                >
                  <td className="py-2.5 pr-4">{r.kind}</td>
                  <td className="py-2.5 pr-4 text-description">{r.source}</td>
                  <td className="py-2.5 pr-4 text-description">
                    {r.reasonCode ?? '—'}
                  </td>
                  <td className="py-2.5 pr-4 text-description">
                    {formatUtcDate(r.appliedAt, DateFormatOption.PARTIAL)}
                  </td>
                  <td className="py-2.5 pr-4 text-description">
                    {r.liftedAt
                      ? formatUtcDate(r.liftedAt, DateFormatOption.PARTIAL)
                      : '—'}
                  </td>
                  <td className="py-2.5 pr-4">
                    {r.isActive ? (
                      <span className="status-badge-cancelled">Active</span>
                    ) : (
                      <span className="status-badge-neutral">Lifted</span>
                    )}
                  </td>
                  <td className="py-2.5">
                    {r.isActive && r.source === 'ManualMlro' ? (
                      <LoadingButton
                        variant="ghost"
                        size="sm"
                        isLoading={isLoading && pendingId === r.id}
                        onClick={() => handleLift(r)}
                      >
                        Lift
                      </LoadingButton>
                    ) : (
                      <span className="text-description">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// MLRO cases
// ──────────────────────────────────────────────────────────────────────────

const CasesCard: React.FC<{
  userId: string
  cases: ComplianceCaseAdminDto[]
}> = ({ userId, cases }) => {
  const toast = useToast()
  const [resolve, { isLoading }] = useBackupCaseResolveMutation()
  const [pendingId, setPendingId] = useState<string | null>(null)

  const openCount = cases.filter(c => c.status === 'Open').length

  const caseTypeToWire: Record<
    ComplianceCaseAdminDto['caseType'],
    | 'edd_approval'
    | 'plausibility_review'
    | 'vuln_autonomy_review'
    | 'tm_alert'
    | 'sof_request'
  > = {
    EddApproval: 'edd_approval',
    PlausibilityReview: 'plausibility_review',
    VulnAutonomyReview: 'vuln_autonomy_review',
    TmAlert: 'tm_alert',
    SofRequest: 'sof_request',
  }

  const handleResolve = async (
    c: ComplianceCaseAdminDto,
    decision: BackupCaseDecision
  ) => {
    if (!c.linkedAssessmentId) {
      toast({
        title: 'Cannot resolve case',
        description:
          'This case has no linked assessment — case.resolved requires linkedAssessmentId.',
        status: 'error',
        position: 'top-right',
      })
      return
    }

    setPendingId(c.id)
    try {
      const result = await resolve({
        investorId: userId,
        body: {
          linkedAssessmentId: c.linkedAssessmentId,
          caseType: caseTypeToWire[c.caseType],
          decision,
          decisionNote: `Operator-resolved via platform backup (decision=${decision}).`,
        },
      }).unwrap()
      toast({
        title: `Case ${decision}`,
        description: `Dispatcher outcome: ${result.outcome}. Domain side effects applied.`,
        status: 'success',
        position: 'top-right',
      })
    } catch {
      toast({
        title: 'Could not resolve case',
        description:
          'See API logs — the dispatcher may have rejected the call.',
        status: 'error',
        position: 'top-right',
      })
    } finally {
      setPendingId(null)
    }
  }

  return (
    <div className="card">
      <SectionHeader
        title="MLRO cases"
        description="CRM-mirrored compliance cases. Open cases can be resolved here when the CRM is offline."
        right={
          openCount > 0 ? (
            <span className="status-badge-processing">{openCount} open</span>
          ) : (
            <span className="status-badge-neutral">None open</span>
          )
        }
      />
      {cases.length === 0 ? (
        <p className="text-description">
          No compliance cases opened for this customer.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-default-small">
            <thead>
              <tr className="text-left text-description border-b border-solid border-brand-light-card-border">
                <th className="py-2.5 pr-4 font-semi">Opened</th>
                <th className="py-2.5 pr-4 font-semi">Type</th>
                <th className="py-2.5 pr-4 font-semi">Assigned</th>
                <th className="py-2.5 pr-4 font-semi">Priority</th>
                <th className="py-2.5 pr-4 font-semi">Summary</th>
                <th className="py-2.5 pr-4 font-semi">Status</th>
                <th className="py-2.5 pr-4 font-semi">Resolved</th>
                <th className="py-2.5 pr-4 font-semi">CRM id</th>
                <th className="py-2.5 font-semi">Actions</th>
              </tr>
            </thead>
            <tbody>
              {cases.map(c => (
                <tr
                  key={c.id}
                  className="border-b border-solid border-brand-light-card-border last:border-b-0 hover:bg-brand-light-background/60 transition-colors align-top"
                >
                  <td className="py-2.5 pr-4 text-description whitespace-nowrap">
                    {formatUtcDate(c.createdAt, DateFormatOption.PARTIAL)}
                  </td>
                  <td className="py-2.5 pr-4">{c.caseType}</td>
                  <td className="py-2.5 pr-4 text-description">
                    {c.assignedRole}
                  </td>
                  <td className="py-2.5 pr-4 text-description">{c.priority}</td>
                  <td className="py-2.5 pr-4 max-w-md whitespace-normal text-description">
                    {c.summary}
                  </td>
                  <td className="py-2.5 pr-4">
                    <CaseStatusPill status={c.status} />
                  </td>
                  <td className="py-2.5 pr-4 text-description whitespace-nowrap">
                    {c.resolvedAt
                      ? formatUtcDate(c.resolvedAt, DateFormatOption.PARTIAL)
                      : '—'}
                  </td>
                  <td className="py-2.5 pr-4 font-mono text-[11px] text-description">
                    {c.crmCaseId ? `${c.crmCaseId.slice(0, 8)}…` : '—'}
                  </td>
                  <td className="py-2.5">
                    {c.status === 'Open' && c.linkedAssessmentId ? (
                      <div className="flex flex-wrap gap-1.5">
                        <LoadingButton
                          variant="primary"
                          size="sm"
                          isLoading={isLoading && pendingId === c.id}
                          onClick={() => handleResolve(c, 'approved')}
                        >
                          Approve
                        </LoadingButton>
                        <LoadingButton
                          variant="warning"
                          size="sm"
                          isLoading={isLoading && pendingId === c.id}
                          onClick={() => handleResolve(c, 'rejected')}
                        >
                          Reject
                        </LoadingButton>
                        <LoadingButton
                          variant="ghost"
                          size="sm"
                          isLoading={isLoading && pendingId === c.id}
                          onClick={() => handleResolve(c, 'deferred')}
                        >
                          Defer
                        </LoadingButton>
                      </div>
                    ) : (
                      <span className="text-description">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// Backup actions — organized as a grid of action cards
// ──────────────────────────────────────────────────────────────────────────

const ActionCard: React.FC<{
  title: string
  description?: string
  children: React.ReactNode
  tone?: 'default' | 'danger'
}> = ({ title, description, children, tone = 'default' }) => (
  <div
    className={`rounded-lg border border-solid p-4 lg:p-5 flex flex-col gap-3 bg-brand-light-card dark:bg-brand-dark-card transition-all ${
      tone === 'danger'
        ? 'border-brand-light-red/40'
        : 'border-brand-light-card-border dark:border-brand-dark-card-border'
    }`}
  >
    <div>
      <h3
        className={`font-semi text-default-small ${
          tone === 'danger' ? 'text-brand-light-red' : ''
        }`}
      >
        {title}
      </h3>
      {description && <p className="text-description mt-1">{description}</p>}
    </div>
    {children}
  </div>
)

const BackupActionsSection: React.FC<{
  userId: string
  data: ComplianceInvestorDto
}> = ({ userId, data }) => {
  const toast = useToast()

  const [applyRestriction, applyRestrictionState] =
    useBackupRestrictionDecisionMutation()
  const [tierChange, tierChangeState] = useBackupTierChangeMutation()
  const [reviewRequired, reviewRequiredState] =
    useBackupReviewRequiredMutation()
  const [sofClose, sofCloseState] = useBackupSofRequestCloseMutation()
  const [customerExit, customerExitState] = useBackupCustomerExitMutation()
  const [strFile, strFileState] = useBackupStrFileMutation()
  const [pepConfirm, pepConfirmState] = useBackupPepConfirmMutation()

  const [restrictionKind, setRestrictionKind] =
    useState<BackupRestrictionKindWire>('tx_over_5k_block')
  const [restrictionReason, setRestrictionReason] = useState('')

  const currentTierLower = data.tier
    ? (data.tier.toLowerCase() as BackupTierWire)
    : null
  const [nextTier, setNextTier] = useState<BackupTierWire>(
    currentTierLower === 'high' ? 'medium' : (currentTierLower ?? 'medium')
  )
  const [tierReason, setTierReason] = useState('')

  const [sofOutcome, setSofOutcome] = useState<BackupSofOutcome>('accepted')
  const [sofNote, setSofNote] = useState('')

  const [exitNote, setExitNote] = useState('')
  const [strNote, setStrNote] = useState('')
  const [pepReason, setPepReason] = useState('')

  const toastSuccess = (title: string, outcome: string) =>
    toast({
      title,
      description: `Dispatcher outcome: ${outcome}.`,
      status: 'success',
      position: 'top-right',
    })

  const toastError = (title: string) =>
    toast({
      title,
      description: 'See API logs for the rejection reason.',
      status: 'error',
      position: 'top-right',
    })

  const handleApplyRestriction = async () => {
    try {
      const result = await applyRestriction({
        investorId: userId,
        body: {
          kind: restrictionKind,
          action: 'apply',
          reasonCode: restrictionReason || undefined,
        },
      }).unwrap()
      toastSuccess('Restriction applied', result.outcome)
      setRestrictionReason('')
    } catch {
      toastError('Could not apply restriction')
    }
  }

  const handleTierChange = async () => {
    try {
      const result = await tierChange({
        investorId: userId,
        body: {
          nextTier,
          reason: tierReason || undefined,
        },
      }).unwrap()
      toastSuccess('Tier changed', result.outcome)
      setTierReason('')
    } catch {
      toastError('Could not change tier')
    }
  }

  const handleReviewRequired = async () => {
    try {
      const result = await reviewRequired(userId).unwrap()
      toastSuccess('Periodic review triggered', result.outcome)
    } catch {
      toastError('Could not trigger periodic review')
    }
  }

  const handleSofClose = async () => {
    try {
      const result = await sofClose({
        investorId: userId,
        body: {
          outcome: sofOutcome,
          decisionNote: sofNote || undefined,
        },
      }).unwrap()
      toastSuccess('SOF request closed', result.outcome)
      setSofNote('')
    } catch {
      toastError('Could not close SOF request')
    }
  }

  const handleCustomerExit = async () => {
    if (
      !window.confirm(
        'Customer exit is terminal — it suspends the account, applies a permanent freeze, and sets compliance state to Exited. Continue?'
      )
    )
      return
    try {
      const result = await customerExit({
        investorId: userId,
        body: { decisionNote: exitNote || undefined },
      }).unwrap()
      toastSuccess('Customer exited', result.outcome)
      setExitNote('')
    } catch {
      toastError('Could not exit customer')
    }
  }

  const handleStrFile = async () => {
    try {
      const result = await strFile({
        investorId: userId,
        body: { decisionNote: strNote || undefined },
      }).unwrap()
      toastSuccess('STR recorded', result.outcome)
      setStrNote('')
    } catch {
      toastError('Could not record STR')
    }
  }

  const handlePepConfirm = async (confirmed: boolean) => {
    if (
      !confirmed &&
      !window.confirm(
        'Un-confirm PEP? Only do this for a false positive you have cleared — it removes the sticky PEP flag that forces EDD at onboarding.'
      )
    )
      return
    try {
      const result = await pepConfirm({
        investorId: userId,
        body: { confirmed, reason: pepReason || undefined },
      }).unwrap()
      toastSuccess(
        confirmed ? 'PEP confirmed' : 'PEP un-confirmed',
        result.outcome
      )
      setPepReason('')
    } catch {
      toastError(
        confirmed ? 'Could not confirm PEP' : 'Could not un-confirm PEP'
      )
    }
  }

  return (
    <div className="card">
      <SectionHeader
        title="Backup actions"
        description="Drive the inbound CRM event types from here when the CRM is unreachable. Each action takes the same code path as a live webhook call — domain state changes idempotently."
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <ActionCard
          title="PEP determination"
          description="Confirm a screened PEP so onboarding forces EDD regardless of the customer's Q10 answer. Sticky — a later Sumsub approval never clears it. Un-confirm only for a cleared false positive."
        >
          <div className="flex flex-col gap-2">
            <p className="text-description text-[13px]">
              Current:{' '}
              {data.pepConfirmed ? (
                <span className="font-semi">
                  Confirmed PEP
                  {data.pepConfirmedAt
                    ? ` · ${formatUtcDate(
                        data.pepConfirmedAt,
                        DateFormatOption.PARTIAL
                      )}`
                    : ''}
                </span>
              ) : (
                <span className="font-semi">Not confirmed</span>
              )}
            </p>
            <StyledInput
              placeholder="Reason (optional, e.g. screening hit reviewed)"
              value={pepReason}
              onChange={e => setPepReason(e.target.value)}
            />
            {data.pepConfirmed ? (
              <LoadingButton
                variant="secondary"
                size="sm"
                isLoading={pepConfirmState.isLoading}
                onClick={() => handlePepConfirm(false)}
              >
                Un-confirm PEP
              </LoadingButton>
            ) : (
              <LoadingButton
                variant="warning"
                size="sm"
                isLoading={pepConfirmState.isLoading}
                onClick={() => handlePepConfirm(true)}
              >
                Confirm PEP
              </LoadingButton>
            )}
          </div>
        </ActionCard>

        <ActionCard
          title="Apply manual restriction"
          description="Lifts of existing ManualMlro restrictions are handled inline in the Restrictions table above."
        >
          <div className="flex flex-col gap-2">
            <StyledSelect
              fullWidth
              value={restrictionKind}
              onChange={e =>
                setRestrictionKind(e.target.value as BackupRestrictionKindWire)
              }
            >
              <option value="deposit_block">Deposit block</option>
              <option value="withdrawal_block">Withdrawal block</option>
              <option value="trade_block">Trade block</option>
              <option value="account_freeze">Account freeze</option>
              <option value="tx_over_5k_block">Tx &gt; 5k block</option>
              <option value="buy_block">Buy block (sell-only)</option>
            </StyledSelect>
            <StyledInput
              placeholder="Reason code (optional)"
              value={restrictionReason}
              onChange={e => setRestrictionReason(e.target.value)}
            />
            <LoadingButton
              variant="warning"
              size="sm"
              isLoading={applyRestrictionState.isLoading}
              onClick={handleApplyRestriction}
            >
              Apply restriction
            </LoadingButton>
          </div>
        </ActionCard>

        <ActionCard
          title="Change risk tier"
          description="Re-scores tier using the supplied reason as the audit trail."
        >
          <div className="flex flex-col gap-2">
            <StyledSelect
              fullWidth
              value={nextTier}
              onChange={e => setNextTier(e.target.value as BackupTierWire)}
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </StyledSelect>
            <StyledInput
              placeholder="Reason (e.g. post-EDD downgrade)"
              value={tierReason}
              onChange={e => setTierReason(e.target.value)}
            />
            <LoadingButton
              variant="primary"
              size="sm"
              isLoading={tierChangeState.isLoading}
              onClick={handleTierChange}
            >
              Change tier
            </LoadingButton>
          </div>
        </ActionCard>

        <ActionCard
          title="Trigger periodic review"
          description="Re-scores using stored questionnaire answers and the current rule set."
        >
          <div className="mt-auto">
            <LoadingButton
              variant="primary"
              size="sm"
              isLoading={reviewRequiredState.isLoading}
              disabled={!data.currentAnswers}
              onClick={handleReviewRequired}
            >
              Run scheduled review
            </LoadingButton>
          </div>
        </ActionCard>

        <ActionCard
          title="Close SOF request"
          description="Logged-only on the platform — Phase 5 will wire the full SOF lifecycle."
        >
          <div className="flex flex-col gap-2">
            <StyledSelect
              fullWidth
              value={sofOutcome}
              onChange={e => setSofOutcome(e.target.value as BackupSofOutcome)}
            >
              <option value="accepted">Accepted</option>
              <option value="rejected">Rejected</option>
              <option value="expired">Expired</option>
            </StyledSelect>
            <StyledInput
              placeholder="Decision note (optional)"
              value={sofNote}
              onChange={e => setSofNote(e.target.value)}
            />
            <LoadingButton
              variant="primary"
              size="sm"
              isLoading={sofCloseState.isLoading}
              onClick={handleSofClose}
            >
              Close SOF request
            </LoadingButton>
          </div>
        </ActionCard>

        <ActionCard
          title="File STR"
          description="Records a Suspicious Transaction Report against the customer's compliance record."
        >
          <div className="flex flex-col gap-2">
            <StyledInput
              placeholder="STR reference (optional)"
              value={strNote}
              onChange={e => setStrNote(e.target.value)}
            />
            <LoadingButton
              variant="warning"
              size="sm"
              isLoading={strFileState.isLoading}
              disabled={!!data.strFiledAt}
              onClick={handleStrFile}
            >
              {data.strFiledAt ? 'STR already filed' : 'Record STR filed'}
            </LoadingButton>
          </div>
        </ActionCard>

        <ActionCard
          title="Customer exit"
          description="Terminal: applies an account freeze and suspends the user account."
          tone="danger"
        >
          <div className="flex flex-col gap-2">
            <StyledTextarea
              rows={2}
              placeholder="Decision note (e.g. post-SAR exit)"
              value={exitNote}
              onChange={e => setExitNote(e.target.value)}
            />
            <LoadingButton
              variant="danger"
              size="sm"
              isLoading={customerExitState.isLoading}
              disabled={data.state === 'Exited'}
              onClick={handleCustomerExit}
            >
              {data.state === 'Exited' ? 'Already exited' : 'Exit customer'}
            </LoadingButton>
          </div>
        </ActionCard>
      </div>
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// Danger zone — full reset
// ──────────────────────────────────────────────────────────────────────────

const ResetCard: React.FC<{ userId: string }> = ({ userId }) => {
  const toast = useToast()
  const [reset, { isLoading }] = useResetComplianceInvestorMutation()
  const [confirmText, setConfirmText] = useState('')

  const handleReset = async () => {
    if (
      !window.confirm(
        "Reset the customer's compliance state? This lifts active restrictions, closes open cases, clears profile flags, and deletes stored questionnaire answers. Assessment history is retained for audit."
      )
    )
      return

    try {
      const result = await reset({
        investorId: userId,
        body: { confirm: confirmText },
      }).unwrap()
      toast({
        title: 'Compliance reset',
        description: `Lifted ${result.restrictionsLifted} restrictions, closed ${result.casesClosed} cases, retained ${result.assessmentHistoryRetained} historical assessments.`,
        status: 'success',
        position: 'top-right',
      })
      setConfirmText('')
    } catch {
      toast({
        title: 'Could not reset compliance',
        description: 'Make sure the confirmation field reads exactly RESET.',
        status: 'error',
        position: 'top-right',
      })
    }
  }

  return (
    <div className="card border-brand-light-red/40">
      <SectionHeader
        title="Danger zone — reset compliance"
        description="Wipes the customer's live compliance state so they can redo onboarding from scratch: lifts active restrictions, closes open cases, clears the profile pointers and flags, and deletes the stored questionnaire answers. Assessment and restriction history rows are kept for regulator audit."
      />
      <div className="flex flex-wrap items-center gap-2">
        <StyledInput
          className="!w-[220px]"
          placeholder='Type "RESET" to confirm'
          value={confirmText}
          onChange={e => setConfirmText(e.target.value)}
        />
        <LoadingButton
          variant="danger"
          size="sm"
          isLoading={isLoading}
          disabled={confirmText !== 'RESET'}
          onClick={handleReset}
        >
          Reset compliance for this customer
        </LoadingButton>
      </div>
    </div>
  )
}

export default Compliance
